import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import {
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material"
import { HTMLReactParserOptions, domToReact, DOMNode } from "html-react-parser"
import { useTranslation } from "react-i18next"

import { ReadMoreButton } from "@/components"
import { useParsedHtml } from "@/hooks"

interface AdditionalDescriptionProps {
  productName: string
  description: string
  additionalDescription: string
}

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (domNode.type === "text") {
      return <Typography variant="body2">{domNode.data}</Typography>
    }
    if (domNode.type === "tag") {
      switch (domNode.tagName) {
        case "ul":
          return (
            <List component="div" dense disablePadding>
              {domToReact(domNode.children as DOMNode[], options)}
            </List>
          )
        case "li":
          return (
            <ListItem component="div" disableGutters disablePadding>
              <ListItemIcon
                sx={{ minWidth: 0, mr: 1, alignSelf: "flex-start", mt: 0.5 }}
              >
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>
                {domToReact(domNode.children as DOMNode[], options)}
              </ListItemText>
            </ListItem>
          )
        case "i":
          return (
            <Typography variant="body2" fontStyle="italic">
              {domToReact(domNode.children as DOMNode[], options)}
            </Typography>
          )
        case "a":
          return (
            <Link
              href={domNode.attribs.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {domToReact(domNode.children as DOMNode[], options)}
            </Link>
          )
        default:
          return <>{domToReact(domNode.children as DOMNode[], options)}</>
      }
    }
  },
}

const AdditionalDescription = ({
  productName,
  description,
  additionalDescription,
}: AdditionalDescriptionProps) => {
  const { t } = useTranslation()
  const parsedAndSanatizedDescription = useParsedHtml(description)
  // The HTML from the CMS contains <b> tags which we don't want to render, so we remove them here
  // A better solution would be to fix this in the CMS or sanitize the HTML properly
  // but this is a quick fix for now
  const cleanedAdditionalDescription = additionalDescription
  const parsedAndSanatizedAdditionalDescription = useParsedHtml(
    cleanedAdditionalDescription,
    options
  )
  return (
    <ReadMoreButton
      buttonLabel={t("readMore", { productName })}
      modalHeadline={productName}
      sx={{ mt: -1, mb: -1, maxWidth: "fit-content", fontSize: "0.875rem" }}
    >
      <Grid container>
        <Typography
          sx={{ mb: 1 }}
          variant="body2"
          data-testid="read-more-modal-description"
        >
          {parsedAndSanatizedDescription}
        </Typography>
        <Grid>
          <Typography
            variant="body2"
            component="h3"
            gutterBottom
            fontWeight="bold"
          >
            {t("allIncludedInPass")}
          </Typography>
          {parsedAndSanatizedAdditionalDescription}
        </Grid>
      </Grid>
    </ReadMoreButton>
  )
}

export default AdditionalDescription
